import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Phone, User, Loader2 } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { formatTime } from '@/lib/dateFormat';

interface Conversation {
  phone: string;
  lastMessage: string;
  lastMessageAt: string;
  direction: string;
  leadId?: string;
  leadName?: string;
  unreadCount: number;
}

interface Message {
  id: string;
  phone: string;
  body: string;
  direction: 'inbound' | 'outbound';
  status: string;
  createdAt: string;
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export function WhatsAppChatPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Inbox poll: 3s (was 5s). Inbound messages now appear within 3s of
  // landing in the DB. No spinner on poll refreshes — only initial load.
  useEffect(() => {
    let cancelled = false;
    const load = async (isInitial: boolean) => {
      try {
        const r = await api.get('/whatsapp/conversations');
        if (!cancelled) setConversations(r.data);
      } catch (err: any) {
        if (isInitial) {
          console.warn('Failed to load WhatsApp conversations:', err?.message);
        }
      } finally {
        if (isInitial && !cancelled) setLoading(false);
      }
    };
    load(true);
    const id = setInterval(() => load(false), 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Open-thread poll: 2s (was 5s). When the user has a thread open, that's
  // where they're staring — fast refresh feels like real-time. Stored in a
  // ref so sendMessage can force-refetch immediately on send instead of
  // waiting for the next tick.
  const reloadMessagesRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => {
    if (!selectedPhone) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/whatsapp/messages/${selectedPhone}`);
        if (!cancelled) setMessages(r.data.data || []);
      } catch (err: any) {
        console.warn('Failed to load WhatsApp messages:', err?.message);
      }
    };
    reloadMessagesRef.current = load;
    load();
    const id = setInterval(load, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedPhone]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !selectedPhone) return;
    const conv = conversations.find(c => c.phone === selectedPhone);

    // Optimistic UI: drop the message into the thread + clear the input
    // BEFORE awaiting Meta. Previously the user stared at their typed
    // text for ~1-2s while the API round-tripped. Now it feels instant.
    // The temp row is marked status='sending' so a future enhancement
    // could show a clock icon until the server confirms.
    const tempId = `temp_${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId,
      phone: selectedPhone,
      body: text,
      direction: 'outbound',
      status: 'sending',
      createdAt: new Date().toISOString(),
    }]);
    setInput('');
    setSending(true);

    try {
      const { data } = await api.post('/whatsapp/send', { phone: selectedPhone, message: text, leadId: conv?.leadId });
      // Force-refetch the thread so the temp row is replaced by the real
      // server-side message id + status, instead of waiting up to 2s for
      // the next poll tick.
      reloadMessagesRef.current?.();
      if (data?.templateFallback) {
        toast('warning', '⏰ 24h window closed — an opener template was sent. Your message will deliver once the customer replies.');
      }
    } catch (err: any) {
      // Remove the optimistic row + restore input so the user can edit
      // and retry.
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setInput(text);
      toast('error', err?.response?.data?.message || 'Failed to send message');
    } finally { setSending(false); }
  };

  const selectedConv = conversations.find(c => c.phone === selectedPhone);

  return (
    <DashboardLayout title="WhatsApp Chat">
      <div className="flex h-[calc(100vh-120px)] bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        {/* Left: Conversations List */}
        <div className="w-80 border-r border-gray-100 flex flex-col flex-shrink-0">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-500" />
              <h2 className="text-sm font-bold text-slate-900">WhatsApp Inbox</h2>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400">No conversations yet</div>
            ) : (
              conversations.map(c => (
                <button key={c.phone} onClick={() => setSelectedPhone(c.phone)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedPhone === c.phone ? 'bg-indigo-50' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{c.leadName || c.phone}</p>
                        <p className="text-xs text-slate-400 truncate">{c.lastMessage}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 ml-2">
                      <span className="text-[10px] text-slate-400">{timeAgo(c.lastMessageAt)}</span>
                      {c.unreadCount > 0 && (
                        <span className="mt-1 h-4 min-w-[16px] rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center px-1">{c.unreadCount}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: Chat Window */}
        <div className="flex-1 flex flex-col">
          {!selectedPhone ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Select a conversation to start chatting</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{selectedConv?.leadName || selectedPhone}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Phone className="h-3 w-3" /> {selectedPhone}</p>
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#ECE5DD]">
                {messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                      msg.direction === 'outbound' ? 'bg-[#DCF8C6] text-slate-800' : 'bg-white text-slate-700'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.body}</p>
                      <p className="text-[10px] text-slate-400 text-right mt-1">
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !sending) sendMessage(); }}
                  placeholder="Type a message..."
                  className="flex-1 h-10 px-4 rounded-full border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                  disabled={sending}
                />
                <button onClick={sendMessage} disabled={sending || !input.trim()}
                  className="h-10 w-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-50">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
